import React, { useRef, useEffect, useState, useCallback } from "react";
import { Button, Typography, Progress } from "antd";

const { Text, Title } = Typography;

// --- Global Constants ---
const GAME_CANVAS_W = 1200;
const GAME_CANVAS_H = 740;
const PREVIEW_W = 280;
const PREVIEW_H = 210;
const POSE_CONFIDENCE_THRESHOLD = 0.5;
const PLAYER_RADIUS = 40;
const PROJECTILE_RADIUS = 30;
const INITIAL_SPAWN_INTERVAL = 1500; // Faster spawning
const MIN_SPAWN_INTERVAL = 500;
const SPAWN_DECREASE_RATE = 100;
const MAX_HITS = 10;
const BALLS_PER_LEVEL = 10; // After every 10 balls dodged, increase ball count

export default function TwoPlayerDodgeball() {
  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const poseEstimator1Ref = useRef(null);
  const poseEstimator2Ref = useRef(null);
  const tempCanvas1Ref = useRef(null);
  const tempCanvas2Ref = useRef(null);
  const lastSpawnTimeRef = useRef(0);
  const spawnIntervalRef = useRef(INITIAL_SPAWN_INTERVAL);
  const animationStartedRef = useRef(false);
  const projectileSpeedMultiplierRef = useRef(1.0);

  const [gameState, setGameState] = useState("ready");
  const [player1Hits, setPlayer1Hits] = useState(0);
  const [player2Hits, setPlayer2Hits] = useState(0);
  const [player1Dodges, setPlayer1Dodges] = useState(0);
  const [player2Dodges, setPlayer2Dodges] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [status, setStatus] = useState("Ready – Click Start to begin.");
  const [mediaPipeLoaded, setMediaPipeLoaded] = useState(false);
  const [winner, setWinner] = useState(null);

  const gameDataRef = useRef({
    player1: { x: GAME_CANVAS_W / 4, y: GAME_CANVAS_H / 2, visible: false },
    player2: {
      x: (GAME_CANVAS_W * 3) / 4,
      y: GAME_CANVAS_H / 2,
      visible: false,
    },
    projectiles: [],
    particles: [],
    currentPlayer1Hits: 0,
    currentPlayer2Hits: 0,
    currentPlayer1Dodges: 0,
    currentPlayer2Dodges: 0,
    currentLevel: 1,
    currentGameState: "ready",
  });

  useEffect(() => {
    gameDataRef.current.currentPlayer1Hits = player1Hits;
    gameDataRef.current.currentPlayer2Hits = player2Hits;
    gameDataRef.current.currentPlayer1Dodges = player1Dodges;
    gameDataRef.current.currentPlayer2Dodges = player2Dodges;
    gameDataRef.current.currentLevel = currentLevel;
    gameDataRef.current.currentGameState = gameState;
  }, [
    player1Hits,
    player2Hits,
    player1Dodges,
    player2Dodges,
    currentLevel,
    gameState,
  ]);

  const createHitEffect = (x, y) => {
    for (let i = 0; i < 15; i++) {
      const angle = (Math.PI * 2 * i) / 15;
      const speed = 2 + Math.random() * 4;
      gameDataRef.current.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 3 + Math.random() * 5,
        color: "#ff4444",
        life: 1,
      });
    }
  };

  const createDodgeEffect = (x, y) => {
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10;
      const speed = 1 + Math.random() * 2;
      gameDataRef.current.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 2 + Math.random() * 3,
        color: "#4ade80",
        life: 1,
      });
    }
  };

  const spawnProjectile = useCallback(() => {
    const { player1, player2 } = gameDataRef.current;
    const currentLevel = gameDataRef.current.currentLevel;
    const ballsToSpawn = currentLevel; // Level 1 = 1 ball, Level 2 = 2 balls, etc.

    // Spawn balls for both players
    for (let i = 0; i < ballsToSpawn; i++) {
      // Spawn ball for Player 1
      if (player1.visible) {
        spawnBallForPlayer("player1", player1);
      }

      // Spawn ball for Player 2
      if (player2.visible) {
        spawnBallForPlayer("player2", player2);
      }
    }
  }, []);

  const spawnBallForPlayer = (targetPlayer, player) => {
    const side = Math.floor(Math.random() * 4);
    let startX, startY;

    // Determine spawn position based on which half of screen
    const minX = targetPlayer === "player1" ? 0 : GAME_CANVAS_W / 2;
    const maxX = targetPlayer === "player1" ? GAME_CANVAS_W / 2 : GAME_CANVAS_W;

    if (side === 0) {
      startX = minX + Math.random() * (maxX - minX);
      startY = -PROJECTILE_RADIUS;
    } else if (side === 1) {
      startX = maxX + PROJECTILE_RADIUS;
      startY = Math.random() * GAME_CANVAS_H;
    } else if (side === 2) {
      startX = minX + Math.random() * (maxX - minX);
      startY = GAME_CANVAS_H + PROJECTILE_RADIUS;
    } else {
      startX = minX - PROJECTILE_RADIUS;
      startY = Math.random() * GAME_CANVAS_H;
    }

    const targetX = player.x;
    const targetY = player.y;

    const dx = targetX - startX;
    const dy = targetY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const baseSpeed = 5 + Math.random() * 3; // Faster initial speed
    const speed = baseSpeed * projectileSpeedMultiplierRef.current;

    const newProjectile = {
      x: startX,
      y: startY,
      vx: (dx / distance) * speed,
      vy: (dy / distance) * speed,
      radius: PROJECTILE_RADIUS,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
      active: true,
      targetPlayer,
    };

    gameDataRef.current.projectiles.push(newProjectile);
  };

  const setupMediaPipe = useCallback(async () => {
    if (!window.Pose) {
      console.error("MediaPipe libraries not loaded.");
      setStatus("Error: MediaPipe libraries were not found.");
      return;
    }

    try {
      // Create temporary canvases for splitting video
      tempCanvas1Ref.current = document.createElement("canvas");
      tempCanvas2Ref.current = document.createElement("canvas");
      tempCanvas1Ref.current.width = 640;
      tempCanvas1Ref.current.height = 720;
      tempCanvas2Ref.current.width = 640;
      tempCanvas2Ref.current.height = 720;

      // Setup Pose 1 for left half (Player 1)
      const pose1 = new window.Pose({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/${file}`,
      });

      pose1.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        minDetectionConfidence: POSE_CONFIDENCE_THRESHOLD,
        minTrackingConfidence: POSE_CONFIDENCE_THRESHOLD,
      });

      pose1.onResults((results) => {
        const landmarks = results.poseLandmarks;
        if (!landmarks) {
          gameDataRef.current.player1.visible = false;
          return;
        }

        const nose = landmarks[0];
        if (nose && nose.visibility > POSE_CONFIDENCE_THRESHOLD) {
          // Map to left half of game canvas with mirrored movement
          const x = (1 - nose.x) * (GAME_CANVAS_W / 2);
          const y = nose.y * GAME_CANVAS_H;
          gameDataRef.current.player1 = { x, y, visible: true };
        } else {
          gameDataRef.current.player1.visible = false;
        }
      });

      poseEstimator1Ref.current = pose1;

      // Setup Pose 2 for right half (Player 2)
      const pose2 = new window.Pose({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/${file}`,
      });

      pose2.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        minDetectionConfidence: POSE_CONFIDENCE_THRESHOLD,
        minTrackingConfidence: POSE_CONFIDENCE_THRESHOLD,
      });

      pose2.onResults((results) => {
        const landmarks = results.poseLandmarks;
        if (!landmarks) {
          gameDataRef.current.player2.visible = false;
          return;
        }

        const nose = landmarks[0];
        if (nose && nose.visibility > POSE_CONFIDENCE_THRESHOLD) {
          // Map to right half of game canvas with mirrored movement
          const x = GAME_CANVAS_W / 2 + (1 - nose.x) * (GAME_CANVAS_W / 2);
          const y = nose.y * GAME_CANVAS_H;
          gameDataRef.current.player2 = { x, y, visible: true };
        } else {
          gameDataRef.current.player2.visible = false;
        }
      });

      poseEstimator2Ref.current = pose2;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
      });

      videoRef.current.srcObject = stream;
      previewVideoRef.current.srcObject = stream;

      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
        previewVideoRef.current.play();

        setStatus(
          "Camera ready! Player 1 on LEFT, Player 2 on RIGHT. Click Start Game."
        );

        // Process both halves of the video
        const processFrame = async () => {
          if (
            !videoRef.current ||
            videoRef.current.paused ||
            videoRef.current.ended
          ) {
            requestAnimationFrame(processFrame);
            return;
          }

          const video = videoRef.current;
          const ctx1 = tempCanvas1Ref.current.getContext("2d");
          const ctx2 = tempCanvas2Ref.current.getContext("2d");

          // Draw left half for Player 1
          ctx2.drawImage(
            video,
            0,
            0,
            video.videoWidth / 2,
            video.videoHeight,
            0,
            0,
            640,
            720
          );
          if (poseEstimator1Ref.current) {
            await poseEstimator1Ref.current.send({
              image: tempCanvas1Ref.current,
            });
          }

          // Draw right half for Player 2
          ctx1.drawImage(
            video,
            video.videoWidth / 2,
            0,
            video.videoWidth / 2,
            video.videoHeight,
            0,
            0,
            640,
            720
          );
          if (poseEstimator2Ref.current) {
            await poseEstimator2Ref.current.send({
              image: tempCanvas2Ref.current,
            });
          }

          requestAnimationFrame(processFrame);
        };

        processFrame();

        if (!animationStartedRef.current) {
          animationStartedRef.current = true;
          gameLoop();
        }
      };
    } catch (err) {
      console.error("Camera access error:", err);
      setStatus("Error: Camera access denied. Please allow camera access.");
    }
  }, []);

  const gameLoopLogic = () => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    const { player1, player2, projectiles, particles } = gameDataRef.current;
    const currentGameState = gameDataRef.current.currentGameState;

    // Clear and draw background
    ctx.clearRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);
    const gradient = ctx.createLinearGradient(0, 0, 0, GAME_CANVAS_H);
    gradient.addColorStop(0, "#0f172a");
    gradient.addColorStop(1, "#1e293b");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);

    // Draw center divider
    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(GAME_CANVAS_W / 2, 0);
    ctx.lineTo(GAME_CANVAS_W / 2, GAME_CANVAS_H);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw grid
    ctx.strokeStyle = "rgba(100, 116, 139, 0.2)";
    ctx.lineWidth = 1;
    for (let i = 0; i < GAME_CANVAS_W; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, GAME_CANVAS_H);
      ctx.stroke();
    }
    for (let i = 0; i < GAME_CANVAS_H; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(GAME_CANVAS_W, i);
      ctx.stroke();
    }

    // Draw particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1;
      p.life -= 0.02;
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = p.life;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Spawn projectiles
    if (currentGameState === "playing") {
      const now = Date.now();
      if (now - lastSpawnTimeRef.current > spawnIntervalRef.current) {
        spawnProjectile();
        lastSpawnTimeRef.current = now;
      }
    }

    // Update and draw projectiles
    for (let i = projectiles.length - 1; i >= 0; i--) {
      const proj = projectiles[i];
      if (!proj.active) continue;

      proj.x += proj.vx;
      proj.y += proj.vy;

      // Check if projectile went out of bounds - count as dodge
      if (
        proj.x < -proj.radius * 2 ||
        proj.x > GAME_CANVAS_W + proj.radius * 2 ||
        proj.y < -proj.radius * 2 ||
        proj.y > GAME_CANVAS_H + proj.radius * 2
      ) {
        if (currentGameState === "playing") {
          const targetPlayer =
            proj.targetPlayer === "player1" ? player1 : player2;
          if (targetPlayer.visible) {
            createDodgeEffect(proj.x, proj.y);

            if (proj.targetPlayer === "player1") {
              setPlayer1Dodges((d) => {
                const newDodges = d + 1;
                const totalDodges =
                  newDodges + gameDataRef.current.currentPlayer2Dodges;

                // Check if we should level up
                if (totalDodges % BALLS_PER_LEVEL === 0) {
                  setCurrentLevel((l) => l + 1);
                  projectileSpeedMultiplierRef.current += 0.15; // Speed increase
                }

                return newDodges;
              });
            } else {
              setPlayer2Dodges((d) => {
                const newDodges = d + 1;
                const totalDodges =
                  newDodges + gameDataRef.current.currentPlayer1Dodges;

                // Check if we should level up
                if (totalDodges % BALLS_PER_LEVEL === 0) {
                  setCurrentLevel((l) => l + 1);
                  projectileSpeedMultiplierRef.current += 0.15; // Speed increase
                }

                return newDodges;
              });
            }
          }
        }

        projectiles.splice(i, 1);
        continue;
      }

      // Check collision with players
      const targetPlayer = proj.targetPlayer === "player1" ? player1 : player2;

      if (targetPlayer.visible && currentGameState === "playing") {
        const dx = targetPlayer.x - proj.x;
        const dy = targetPlayer.y - proj.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < PLAYER_RADIUS + proj.radius) {
          proj.active = false;
          projectiles.splice(i, 1);
          createHitEffect(proj.x, proj.y);

          // Update hits
          if (proj.targetPlayer === "player1") {
            setPlayer1Hits((h) => {
              const newHits = h + 1;
              if (newHits >= MAX_HITS) {
                setGameState("finished");
                setWinner("Player 2");
                setStatus("Player 2 Wins!");
              }
              return newHits;
            });
          } else {
            setPlayer2Hits((h) => {
              const newHits = h + 1;
              if (newHits >= MAX_HITS) {
                setGameState("finished");
                setWinner("Player 1");
                setStatus("Player 1 Wins!");
              }
              return newHits;
            });
          }
          continue;
        }
      }

      // Draw projectile
      ctx.save();
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, proj.radius + 5, 0, Math.PI * 2);
      const glowGradient = ctx.createRadialGradient(
        proj.x,
        proj.y,
        0,
        proj.x,
        proj.y,
        proj.radius + 5
      );
      glowGradient.addColorStop(0, proj.color);
      glowGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = glowGradient;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(proj.x, proj.y, proj.radius, 0, Math.PI * 2);
      const projGradient = ctx.createRadialGradient(
        proj.x - proj.radius * 0.3,
        proj.y - proj.radius * 0.3,
        0,
        proj.x,
        proj.y,
        proj.radius
      );
      projGradient.addColorStop(0, "#fff");
      projGradient.addColorStop(0.5, proj.color);
      projGradient.addColorStop(1, "#000");
      ctx.fillStyle = projGradient;
      ctx.fill();
      ctx.restore();
    }

    // Draw players
    const drawPlayer = (player, color) => {
      if (!player.visible) return;

      ctx.save();
      ctx.beginPath();
      ctx.arc(player.x, player.y, PLAYER_RADIUS + 10, 0, Math.PI * 2);
      const playerGlow = ctx.createRadialGradient(
        player.x,
        player.y,
        0,
        player.x,
        player.y,
        PLAYER_RADIUS + 10
      );
      playerGlow.addColorStop(0, `${color}80`);
      playerGlow.addColorStop(1, `${color}00`);
      ctx.fillStyle = playerGlow;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(player.x, player.y, PLAYER_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    };

    drawPlayer(player1, "#3b82f6");
    drawPlayer(player2, "#f43f5e");

    // Draw player labels
    ctx.fillStyle = "#fff";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    ctx.fillText("PLAYER 1", GAME_CANVAS_W / 4, 40);
    ctx.fillText("PLAYER 2", (GAME_CANVAS_W * 3) / 4, 40);

    // Game over overlay
    if (currentGameState === "finished") {
      ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
      ctx.fillRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);

      ctx.fillStyle = winner === "Player 1" ? "#3b82f6" : "#f43f5e";
      ctx.font = "bold 64px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor =
        winner === "Player 1"
          ? "rgba(59, 130, 246, 0.5)"
          : "rgba(244, 63, 94, 0.5)";
      ctx.shadowBlur = 20;

      if (winner === "Player 1") {
        ctx.fillText(
          `Player 1 WINS!`,
          GAME_CANVAS_W / 2,
          GAME_CANVAS_H / 2 - 180
        );
      } else {
        ctx.fillText(
          `Player 2 WINS!`,
          GAME_CANVAS_W / 2,
          GAME_CANVAS_H / 2 - 180
        );
      }

      ctx.shadowBlur = 0;

      ctx.font = "bold 32px Arial";
      ctx.fillStyle = "#3b82f6";
      ctx.textAlign = "left";
      ctx.fillText(
        "Player 1:",
        GAME_CANVAS_W / 2 - 280,
        GAME_CANVAS_H / 2 + 10
      );
      ctx.fillStyle = "#fff";
      ctx.fillText(
        `${gameDataRef.current.currentPlayer1Hits} Hits | ${gameDataRef.current.currentPlayer1Dodges} Dodges`,
        GAME_CANVAS_W / 2 - 100,
        GAME_CANVAS_H / 2 + 10
      );

      ctx.fillStyle = "#f43f5e";
      ctx.fillText(
        "Player 2:",
        GAME_CANVAS_W / 2 - 280,
        GAME_CANVAS_H / 2 + 60
      );
      ctx.fillStyle = "#fff";
      ctx.fillText(
        `${gameDataRef.current.currentPlayer2Hits} Hits | ${gameDataRef.current.currentPlayer2Dodges} Dodges`,
        GAME_CANVAS_W / 2 - 100,
        GAME_CANVAS_H / 2 + 60
      );

      ctx.font = "24px Arial";
      ctx.fillStyle = "#94a3b8";
      ctx.textAlign = "center";
      ctx.fillText(
        'Click "START GAME" to play again',
        GAME_CANVAS_W / 2,
        GAME_CANVAS_H / 2 + 170
      );
    }
  };

  const gameLoop = () => {
    gameLoopLogic();
    rafRef.current = requestAnimationFrame(gameLoop);
  };

  const handleStartGame = () => {
    if (gameState === "playing" || !mediaPipeLoaded) return;

    setPlayer1Hits(0);
    setPlayer2Hits(0);
    setPlayer1Dodges(0);
    setPlayer2Dodges(0);
    setCurrentLevel(1);
    setWinner(null);
    setGameState("playing");

    gameDataRef.current.projectiles = [];
    gameDataRef.current.particles = [];
    lastSpawnTimeRef.current = Date.now();
    spawnIntervalRef.current = INITIAL_SPAWN_INTERVAL;
    projectileSpeedMultiplierRef.current = 1.0;

    setStatus("Dodge the projectiles! First to 10 hits loses!");
  };

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = () =>
          reject(new Error(`Failed to load script: ${src}`));
        document.body.appendChild(script);
      });
    };

    const loadMediaPipe = async () => {
      try {
        setStatus("Loading MediaPipe libraries...");
        await loadScript(
          "https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/pose.js"
        );

        setMediaPipeLoaded(true);
        setStatus("Libraries loaded. Requesting camera access...");
        setupMediaPipe();
      } catch (error) {
        console.error("MediaPipe Loading Error:", error);
        setStatus(
          `Error loading required libraries. Check your connection or console.`
        );
      }
    };

    loadMediaPipe();

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
      if (poseEstimator1Ref.current) {
        poseEstimator1Ref.current.close();
      }
      if (poseEstimator2Ref.current) {
        poseEstimator2Ref.current.close();
      }
    };
  }, [setupMediaPipe]);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#0f172a",
        color: "#fff",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <video ref={videoRef} style={{ display: "none" }} playsInline muted />

      {!mediaPipeLoaded ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            padding: 50,
          }}
        >
          <h2 style={{ color: "#fff", fontSize: "2em" }}>
            Loading Game Resources...
          </h2>
          <p style={{ color: "#94a3b8" }}>{status}</p>
          <Progress
            type="circle"
            percent={100}
            width={80}
            status="active"
            style={{ marginTop: 20 }}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              minWidth: 300,
              background: "#1e293b",
              padding: 16,
              boxShadow: "4px 0 10px rgba(0,0,0,0.5)",
            }}
          >
            <Title
              level={3}
              style={{ color: "#fff", marginTop: 0, marginBottom: 16 }}
            >
              Two Player Dodgeball
            </Title>

            <Text
              style={{
                fontSize: 12,
                fontWeight: "bold",
                color: "#e2e8f0",
                display: "block",
                marginBottom: 16,
              }}
            >
              {status}
            </Text>

            <Button
              onClick={handleStartGame}
              disabled={gameState === "playing"}
              type="primary"
              //   danger
              block
              size="large"
              style={{ fontWeight: "bold", marginBottom: 16 }}
            >
              {gameState === "ready" || gameState === "finished"
                ? "Start Game"
                : "Playing..."}
            </Button>

            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  flex: 1,
                  background: "rgba(59, 130, 246, 0.2)",
                  padding: 8,
                  borderRadius: 8,
                  border: "2px solid rgba(59, 130, 246, 0.5)",
                }}
              >
                <Text
                  style={{
                    color: "#60a5fa",
                    fontSize: 14,
                    fontWeight: "bold",
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Player 1
                </Text>
                <br/>
                <div
                  style={{
                    // color: "#fff",
                    color: "#be5420ff",
                    fontSize: 20,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {player1Hits}/{MAX_HITS}
                </div>
                <Text
                  style={{
                    color: "#94a3b8",
                    // color: "#be5420ff",
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Hits
                </Text>
                <br/>
                <div
                  style={{
                    color: "#4ade80",
                    fontSize: 20,
                    fontWeight: "bold",
                    textAlign: "center",
                    marginTop: 4,
                  }}
                >
                  {player1Dodges}
                </div>
                <Text
                  style={{
                    color: "#94a3b8",
                    fontSize: 11,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Dodges
                </Text>
              </div>

              <div
                style={{
                  flex: 1,
                  background: "rgba(244, 63, 94, 0.2)",
                  padding: 8,
                  borderRadius: 8,
                  border: "2px solid rgba(244, 63, 94, 0.5)",
                }}
              >
                <Text
                  style={{
                    color: "#f43f5e",
                    fontSize: 14,
                    fontWeight: "bold",
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Player 2
                </Text>
                <br/>
                <div
                  style={{
                    // color: "#fff",
                    color: "#be5420ff",
                    fontSize: 20,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {player2Hits}/{MAX_HITS}
                </div>
                <Text
                  style={{
                    color: "#94a3b8",
                    // color: "#be5420ff",
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Hits
                </Text>
                <br/>
                <div
                  style={{
                    color: "#4ade80",
                    fontSize: 20,
                    fontWeight: "bold",
                    textAlign: "center",
                    marginTop: 4,
                  }}
                >
                  {player2Dodges}
                </div>
                <Text
                  style={{
                    color: "#94a3b8",
                    fontSize: 11,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  Dodges
                </Text>
              </div>
            </div>

            <Text
              style={{
                color: "#64748b",
                fontSize: 12,
                display: "block",
                marginBottom: 8,
              }}
            >
              Camera Preview:
            </Text>

            <div
              style={{
                width: "100%",
                height: PREVIEW_H,
                border: "2px solid #334155",
                borderRadius: "8px",
                overflow: "hidden",
                background: "#000",
                marginBottom: 8,
                position: "relative",
              }}
            >
              <video
                ref={previewVideoRef}
                style={{
                  transform: "scaleX(-1)",
                  width: "100%",
                  height: "100%",
                }}
                playsInline
                muted
              />
              {/* Divider line on preview */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  width: "3px",
                  height: "100%",
                  background: "rgba(255, 255, 255, 0.8)",
                  transform: "translateX(-50%)",
                  boxShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: "25%",
                  transform: "translateX(-50%)",
                  color: "#3b82f6",
                  fontWeight: "bold",
                  fontSize: 16,
                  textShadow: "0 0 8px black, 0 0 4px black",
                }}
              >
                P1
              </div>
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: "75%",
                  transform: "translateX(-50%)",
                  color: "#f43f5e",
                  fontWeight: "bold",
                  fontSize: 16,
                  textShadow: "0 0 8px black, 0 0 4px black",
                }}
              >
                P2
              </div>
            </div>

            <div
              style={{
                background: "rgba(74, 222, 128, 0.2)",
                padding: 8,
                borderRadius: 6,
                border: "1px solid rgba(74, 222, 128, 0.4)",
                marginBottom: 12,
                textAlign: "center",
              }}
            >
              <Text
                style={{
                  color: "#4ade80",
                  fontSize: 14,
                  fontWeight: "bold",
                  display: "block",
                }}
              >
                Level {currentLevel}
              </Text>
              <Text
                style={{ color: "#94a3b8", fontSize: 11, display: "block" }}
              >
                {currentLevel} ball{currentLevel > 1 ? "s" : ""} per wave •
                Level up every {BALLS_PER_LEVEL} dodges
              </Text>
            </div>

            <div
              style={{
                background: "rgba(59, 130, 246, 0.1)",
                padding: 12,
                borderRadius: 8,
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              <Text
                style={{
                  color: "#60a5fa",
                  display: "block",
                  fontWeight: "bold",
                  marginBottom: 8,
                  fontSize: 13,
                }}
              >
                How to Play:
              </Text>
              <Text
                style={{
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                • Player 1 stands on camera LEFT
              </Text>
              <Text
                style={{
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                • Player 2 stands on camera RIGHT
              </Text>
              <Text
                style={{
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                • Move your HEAD to dodge projectiles
              </Text>
              <Text
                style={{
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                • Each level spawns more balls per wave
              </Text>
              <Text
                style={{
                  color: "#cbd5e1",
                  display: "block",
                  marginBottom: 4,
                  fontSize: 12,
                }}
              >
                • First to 10 hits LOSES
              </Text>
              <Text
                style={{ color: "#cbd5e1", display: "block", fontSize: 12 }}
              >
                • Balls keep moving until they exit!
              </Text>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
            }}
          >
            <div
              style={{
                position: "relative",
                width: GAME_CANVAS_W,
                height: GAME_CANVAS_H,
                boxShadow: "0 0 40px rgba(244, 63, 94, 0.3)",
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid rgba(244, 63, 94, 0.5)",
              }}
            >
              <canvas
                ref={canvasRef}
                width={GAME_CANVAS_W}
                height={GAME_CANVAS_H}
                style={{ borderRadius: "12px" }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
